import React, { FunctionComponent, ReactNode, MouseEvent, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

import { SeoLink_linkData } from './__generated__/SeoLink_linkData.graphql';

export const PARTIALLY_LINKABLE_LINK = 'PARTIALLY_LINKABLE_LINK';
export const ALWAYS_LINKABLE_LINK = 'ALWAYS_LINKABLE_LINK';

type CustomLinkData = { path: string; isLinkable?: boolean };
type Props = {
    children: ReactNode;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    linkData: SeoLink_linkData | CustomLinkData;
    dataTn: string;
    dataPk?: string;
    className?: string;
    variant?: typeof PARTIALLY_LINKABLE_LINK | typeof ALWAYS_LINKABLE_LINK;
    style?: React.CSSProperties;
    key?: string;
    role?: string;
    tabIndex?: number;
    target?: string;
};

export const SeoLinkComponent: FunctionComponent<Props> = ({
    children,
    linkData,
    onClick = () => {},
    dataTn,
    dataPk,
    className,
    variant = ALWAYS_LINKABLE_LINK,
    style,
    key,
    role = 'link',
    tabIndex = 0,
    target,
}) => {
    const { path, isLinkable } = linkData;
    const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
    const alwaysLinkableLink = useRef<HTMLAnchorElement>(null);
    const addHref = (): void => {
        if (path && alwaysLinkableLink?.current) {
            alwaysLinkableLink.current.href = path;
        }
    };
    const removeHref = (): void => {
        if (alwaysLinkableLink?.current) {
            alwaysLinkableLink.current.removeAttribute('href');
        }
    };

    if (!path) {
        return <>{children}</>;
    }

    if (isLinkable) {
        return (
            <a
                role={role}
                tabIndex={tabIndex}
                href={path}
                onClick={onClick}
                data-tn={dataTn}
                data-pk={dataPk}
                className={className}
                style={style}
                key={key}
                ref={alwaysLinkableLink}
                rel={rel}
                target={target}
            >
                {children}
            </a>
        );
    }

    if (variant === PARTIALLY_LINKABLE_LINK) {
        return (
            <span
                onClick={onClick}
                role={role} // links that aren't anchors need a role
                tabIndex={tabIndex} // links that aren't buttons or anchors need to be focusable
                data-tn={dataTn}
                data-pk={dataPk}
                className={className}
                style={style}
                key={key}
            >
                {children}
            </span>
        );
    }

    if (variant === ALWAYS_LINKABLE_LINK) {
        return (
            <a
                onClick={(e: MouseEvent<HTMLElement>) => {
                    onClick(e);
                    addHref();
                }}
                tabIndex={tabIndex} // anchors w/o hrefs need to be focusable
                role={role} // anchors w/o hrefs need role
                onFocus={addHref}
                onBlur={removeHref}
                onMouseOver={addHref}
                onMouseOut={removeHref}
                onTouchStart={addHref}
                onTouchEnd={removeHref}
                data-tn={dataTn}
                data-pk={dataPk}
                className={className}
                style={style}
                ref={alwaysLinkableLink}
                key={key}
                rel={rel}
                target={target}
            >
                {children}
            </a>
        );
    }

    return null;
};

export const SeoLink = createFragmentContainer(SeoLinkComponent, {
    linkData: graphql`
        fragment SeoLink_linkData on LinkData {
            path
            isLinkable
        }
    `,
});
